import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import Layout from "../Util/Layout";

// Each entry is its own timeline node. The promotion at ALA Engineering shows up as a separate node above the SE1 stint.
const careerMilestones = [
    {
        startYear: "June 2026",
        endYear: "Present",
        title: "Technical Product Manager",
        company: "ALA Engineering",
        location: "Lincoln, NE",
        description:
            "Promoted after naturally taking on more leadership responsibility as an engineer. Leading cross-functional product execution across embedded systems, autonomy, web platforms, manufacturing, and testing, making sure the company's technical efforts stay clear, connected, and focused on measurable outcomes. Acting as a bridge between teams to coordinate decisions, resolve blockers, and maintain momentum across many parallel projects. Still contributing as an individual contributor to autonomous vehicle code in C++ as needed, and serving as the product owner for machine learning pipelines from initial data collection through to deployed models.",
    },
    {
        startYear: "July 2025",
        endYear: "June 2026",
        title: "Software Engineer 2, Autonomy and Machine Learning",
        company: "ALA Engineering",
        location: "Lincoln, NE",
        description:
            "Promoted to Software Engineer 2 with a continued focus on the autonomy stack, and a new responsibility to lead machine learning model and pipeline development. Acted as a mentor to newer engineers, teaching C++, company development standards, and machine learning principals.",
    },
    {
        startYear: "June 2024",
        endYear: "July 2025",
        title: "Software Engineer 1, Autonomy",
        company: "ALA Engineering",
        location: "Lincoln, NE",
        description:
            "Joined the ALA Engineering autonomy team. Built vision processing models and contributed to the sensor blending pipeline (camera, lidar, radar, GPS) for the self-driving cattle feeding truck before being promoted to SE2.",
    },
    {
        startYear: "May 2023",
        endYear: "June 2024",
        title: "Junior Software Engineer",
        company: "Drone Amplified",
        description:
            "Spearheaded the development of web applications and scripts for customer troubleshooting, data collection, and inventory automation. The inventory tool suite saves the company ~20 hours of labor (~$1,000) weekly, and the external tools improved customer relations and gathered data later used for ML model training.",
    },
    {
        startYear: "January 2022",
        endYear: "January 2023",
        title: "Software Engineering Intern",
        company: "Sandhills Global",
        description:
            "Developed and tested full-stack .NET applications backed by SQL databases. Worked in a SCRUM environment alongside experienced engineers to maintain existing software and ship new projects.",
    },
    {
        startYear: "April 2021",
        endYear: "October 2021",
        title: "Software Development Intern",
        company: "Diabase Engineering",
        description:
            "Engineered Python plugins that made existing 3D printing applications compatible with Diabase machines, and launched a full-stack ReactJS application for G-code manipulation. Got hands-on with advanced 3D printing in an emerging market.",
    },
];

const SPINE_COLOR = "#D8DCE5";
const PANEL_BG = "#F5F6F8";
const PANEL_BG_CURRENT = "#EEF1FF"; // subtle accent for current role
const CONNECTOR_SIZE = 12;

const TimelineEntry = ({ milestone, side, isCurrent, isMobile }) => {
    const dateLabel = `${milestone.startYear} — ${milestone.endYear}`;
    const onLeft = side === "left" && !isMobile;
    const onRight = side === "right" && !isMobile;
    const bg = isCurrent ? PANEL_BG_CURRENT : PANEL_BG;

    return (
        <Box sx={{ position: "relative", mb: { xs: 4, md: 6 } }}>
            {/* Panel */}
            <Box
                sx={{
                    width: {
                        xs: "calc(100% - 56px)",
                        md: "calc(50% - 28px)",
                    },
                    ml: {
                        xs: "56px",
                        md: onLeft ? 0 : "auto",
                    },
                    mr: {
                        xs: 0,
                        md: onLeft ? "auto" : 0,
                    },
                    p: 3,
                    bgcolor: bg,
                    borderRadius: 2,
                    position: "relative",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
            >
                {/* Connector arrow pointing to the spine. Desktop variants on left/right; mobile always points left. */}
                {onLeft && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 24,
                            right: -CONNECTOR_SIZE,
                            width: 0,
                            height: 0,
                            borderTop: `${CONNECTOR_SIZE / 1.5}px solid transparent`,
                            borderBottom: `${CONNECTOR_SIZE / 1.5}px solid transparent`,
                            borderLeft: `${CONNECTOR_SIZE}px solid ${bg}`,
                        }}
                    />
                )}
                {onRight && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 24,
                            left: -CONNECTOR_SIZE,
                            width: 0,
                            height: 0,
                            borderTop: `${CONNECTOR_SIZE / 1.5}px solid transparent`,
                            borderBottom: `${CONNECTOR_SIZE / 1.5}px solid transparent`,
                            borderRight: `${CONNECTOR_SIZE}px solid ${bg}`,
                        }}
                    />
                )}
                {isMobile && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 24,
                            left: -CONNECTOR_SIZE,
                            width: 0,
                            height: 0,
                            borderTop: `${CONNECTOR_SIZE / 1.5}px solid transparent`,
                            borderBottom: `${CONNECTOR_SIZE / 1.5}px solid transparent`,
                            borderRight: `${CONNECTOR_SIZE}px solid ${bg}`,
                        }}
                    />
                )}

                <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            color: "primary.main",
                            fontWeight: 700,
                            letterSpacing: 1.2,
                            lineHeight: 1,
                        }}
                    >
                        {dateLabel}
                    </Typography>
                    {isCurrent && (
                        <Box
                            sx={{
                                ml: 1.5,
                                px: 1,
                                py: 0.25,
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                borderRadius: 1,
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: 1,
                            }}
                        >
                            CURRENT
                        </Box>
                    )}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {milestone.title}
                </Typography>
                <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mb: 1.5 }}
                >
                    {milestone.company}
                    {milestone.location ? ` · ${milestone.location}` : ""}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6 }}>
                    {milestone.description}
                </Typography>
            </Box>

            {/* Dot on the spine */}
            <Box
                sx={{
                    position: "absolute",
                    top: 24,
                    left: { xs: "28px", md: "50%" },
                    transform: "translate(-50%, 0)",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    bgcolor: isCurrent ? "primary.main" : "background.default",
                    border: "3px solid",
                    borderColor: "primary.main",
                    boxShadow: "0 0 0 4px #ffffff",
                    zIndex: 1,
                }}
            />
        </Box>
    );
};

const EngineeringExperience = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <Layout>
            <Box p={3}>
                <Box mb={5}>
                    <Typography variant="h3" component="h1" gutterBottom>
                        Engineering Experience
                    </Typography>
                    <Typography
                        color="text.secondary"
                        sx={{ fontSize: "1.05rem", lineHeight: 1.6 }}
                    >
                        A timeline of the roles that shaped my engineering career, from
                        early internships to leading technical product today.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        maxWidth: 1000,
                        mx: "auto",
                        py: 2,
                    }}
                >
                    {/* The spine */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            bottom: 0,
                            left: { xs: "28px", md: "50%" },
                            transform: "translateX(-50%)",
                            width: "2px",
                            bgcolor: SPINE_COLOR,
                        }}
                    />

                    {careerMilestones.map((milestone, index) => (
                        <TimelineEntry
                            key={`${milestone.company}-${milestone.startYear}`}
                            milestone={milestone}
                            side={index % 2 === 0 ? "right" : "left"}
                            isCurrent={milestone.endYear === "Present"}
                            isMobile={isMobile}
                        />
                    ))}
                </Box>
            </Box>
        </Layout>
    );
};

export default EngineeringExperience;
